/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import {
  ProfileOccupationChoices_DemandLevel as DemandLevel,
  ProfileOccupationChoices_FeatureTag as FeatureTag,
} from '__generated__/graphql-types';
import values from 'lodash/values';

import { Grid, InlineNotification, Typography, Typography2, breakpoints, useMediaQuery } from '@coursera/cds-core';
import { useTracker } from '@coursera/event-pulse/react';

import { useProfileOccupationChoices } from 'bundles/employability-common/queries/hooks/useProfileOccupationChoices';
import { convertProfileOccupationChoiceToPreferenceNaptimeType } from 'bundles/employability-common/queries/utils/convertProfileOccupationChoiceToPreferenceNaptimeType';
import DropdownSelect from 'bundles/onboarding-2022/components/shared/DropdownSelect';
import GenericSelect from 'bundles/onboarding-2022/components/shared/GenericSelect';
import LearningGoalCardSelect from 'bundles/onboarding-2022/components/shared/LearningGoalCardSelect';
import RoleOptionSearchAndSelectExtended from 'bundles/onboarding-2022/components/shared/RoleOptionSearchAndSelectExtended';
import UserEntryAutoComplete from 'bundles/onboarding-2022/components/shared/UserEntryAutoComplete';
import { ViewFormVisibilityTracker } from 'bundles/onboarding-2022/components/shared/ViewFormVisibilityTracker';
import { HACK_SURVEY_LENGTH } from 'bundles/onboarding-2022/data/constants';
import { isNonTerminalSurvey } from 'bundles/onboarding-2022/data/onboardingPageUtils';
import type { OnChangeEvent } from 'bundles/onboarding-2022/types/OnChangeInput';
import type {
  Answer,
  QuestionAnswer,
  SurveyData,
  SurveyQuestion,
  SurveyRequestAnswers,
} from 'bundles/onboarding-2022/types/Survey';
import type { SelectedRole } from 'bundles/onboarding-common/types';
import { QuestionTypes } from 'bundles/survey-form/constants/SurveyFormConstants';

type PropsFromCaller = {
  currentPageIndex: number;
  currentSectionSurveyData: SurveyData | undefined;
  answers: SurveyRequestAnswers;
  onChange: (answer: QuestionAnswer) => void;
  setValidationErrorMessage?: (message?: string) => void;
  validationErrorMessage?: string;
};

type Props = PropsFromCaller;

const styles = {
  contentContainer: () => css`
    justify-content: center;
  `,
  sectionTitleContainer: css`
    align-items: center;
    margin: 0 0 var(--cds-spacing-400);

    ${breakpoints.down('xs')} {
      margin-bottom: var(--cds-spacing-200);
    }
  `,
  sectionTitle: css`
    max-width: 760px;
    margin: 0 auto;
    text-align: center;
  `,
  sectionQuestions: css`
    margin: 0 0 var(--cds-spacing-400);
    align-items: center;
    justify-content: center;
  `,
  validationErrorMessage: css`
    width: fit-content;
  `,
  errorMessageContainer: css`
    display: flex;
    justify-content: center;
    transition: all 250ms ease;
  `,
  errorMessageContainerMargin: css`
    margin-bottom: var(--cds-spacing-400);
  `,
  errorMessageMobile: css`
    position: sticky;
    bottom: 80px;
  `,
};

function normalizeValue(val: string | string[] | Record<string, string>): string[] {
  if (!val) {
    return [];
  } else if (typeof val === 'string') {
    return [val];
  } else if (Array.isArray(val)) {
    return val;
  } else {
    return values(val);
  }
}

function trimEmpty(val: string[]) {
  return val.filter((v) => !!v);
}

const OnboardingPageContent = (props: Props) => {
  const {
    currentSectionSurveyData,
    answers,
    onChange,
    currentPageIndex,
    setValidationErrorMessage,
    validationErrorMessage,
  } = props;
  const isSmDown = useMediaQuery(breakpoints.down('sm'));
  const { occupations: occupationsData } = useProfileOccupationChoices({
    occupationChoiceFilter: {
      occupationTags: [
        { demandLevel: DemandLevel.Low, featureTag: FeatureTag.BaselineV2 },
        { demandLevel: DemandLevel.High, featureTag: FeatureTag.BaselineV2 },
      ],
    },
  });

  const occupations = convertProfileOccupationChoiceToPreferenceNaptimeType(occupationsData);

  const getValue = (sectionId: string, questionId: string): Answer => {
    if (answers[sectionId] && answers[sectionId][questionId]) {
      return answers[sectionId][questionId];
    } else {
      return '';
    }
  };

  const track = useTracker();

  if (isNonTerminalSurvey(currentSectionSurveyData)) {
    const { questions, title, sectionId } = currentSectionSurveyData;
    const formEventData = {
      name: 'onboarding_consumer_multi_page_2022',
      currentStep: currentPageIndex,
      totalSteps: HACK_SURVEY_LENGTH,
      fieldsAvailable: questions.length,
    } as const;

    return (
      <ViewFormVisibilityTracker {...formEventData}>
        <Grid container css={styles.contentContainer}>
          <Grid item css={styles.sectionTitleContainer}>
            <Typography variant="h1">{title}</Typography>
          </Grid>
          {!isSmDown && (
            <Grid
              item
              aria-live="polite"
              xs={12}
              css={[styles.errorMessageContainer, validationErrorMessage && styles.errorMessageContainerMargin]}
            >
              {validationErrorMessage && (
                <InlineNotification severity="error" role="alert" css={styles.validationErrorMessage}>
                  <Typography2 component="p">{validationErrorMessage}</Typography2>
                </InlineNotification>
              )}
            </Grid>
          )}
          <Grid item container css={styles.sectionQuestions}>
            {questions.map((question: SurveyQuestion) => {
              if (question.type === QuestionTypes.CHECKBOX && question.configurations?.collectionId) {
                return (
                  <Grid item key={`role-option-search-and-select-${question.id}`}>
                    <RoleOptionSearchAndSelectExtended
                      onChange={(selectedRoles: SelectedRole[]) => onChange({ [question.id]: selectedRoles })}
                      defaultValue={null}
                      setValidationErrorMessage={setValidationErrorMessage}
                    />
                  </Grid>
                );
              } else if (
                question.type === QuestionTypes.MULTIPLE_CHOICE &&
                question.configurations?.useLearningGoalCard
              ) {
                const value = getValue(sectionId, question.id) as string;
                return (
                  <Grid item key={`learning-goal-select-${question.id}`}>
                    <LearningGoalCardSelect
                      value={value}
                      questionId={question.id}
                      question={question.prompt}
                      options={question.options}
                      onChange={(changeObj: OnChangeEvent) => {
                        const newValue = trimEmpty(normalizeValue(changeObj[question.id]))[0] || '';
                        track('change_radio', {
                          form: formEventData,
                          radio: {
                            id: question.id,
                            value: newValue,
                            label: question.prompt,
                            isRequired: false,
                          },
                        });
                        onChange(changeObj);
                      }}
                    />
                  </Grid>
                );
              } else if (question.type === QuestionTypes.MULTIPLE_CHOICE) {
                const value = getValue(sectionId, question.id) as string;
                return (
                  <Grid item key={`generic-select-${question.id}`}>
                    <GenericSelect
                      value={value}
                      questionId={question.id}
                      question={question.prompt}
                      options={question.options}
                      onChange={(changeObj: OnChangeEvent) => {
                        const newValue = trimEmpty(normalizeValue(changeObj[question.id]))[0] || '';
                        track('change_radio', {
                          form: formEventData,
                          radio: {
                            id: question.id,
                            value: newValue,
                            label: question.prompt,
                            isRequired: false,
                          },
                        });
                        onChange(changeObj);
                      }}
                    />
                  </Grid>
                );
              } else if (question.type === QuestionTypes.TYPEAHEAD) {
                const value = getValue(sectionId, question.id) as Record<string, string>;
                return (
                  <Grid item key={`auto-complete-${question.id}`} xs={12}>
                    <UserEntryAutoComplete
                      question={question.prompt}
                      questionId={question.id}
                      onChange={(changeObj: OnChangeEvent) => {
                        const newValue = trimEmpty(normalizeValue(changeObj[question.id]));
                        track('change_select', {
                          form: formEventData,
                          select: {
                            name: question.id,
                            value: newValue,
                            label: question.prompt,
                            isRequired: false,
                          },
                        });
                        onChange(changeObj);
                      }}
                      value={value}
                      occupations={occupations}
                    />
                  </Grid>
                );
              } else if (question.type === QuestionTypes.DROPDOWN) {
                return (
                  <Grid item key={`dropdown-select-${question.id}`}>
                    <DropdownSelect
                      value={getValue(sectionId, question.id) as string}
                      questionId={question.id}
                      question={question.prompt}
                      options={question.options}
                      onChange={(changeObj: OnChangeEvent) => {
                        const newValue = trimEmpty(normalizeValue(changeObj[question.id]));
                        track('change_select', {
                          form: formEventData,
                          select: {
                            name: question.id,
                            value: newValue,
                            label: question.prompt,
                            isRequired: false,
                          },
                        });
                        onChange(changeObj);
                      }}
                    />
                  </Grid>
                );
              } else {
                return null;
              }
            })}
          </Grid>
          {isSmDown && (
            <Grid
              item
              aria-live="polite"
              xs={12}
              css={[
                styles.errorMessageContainer,
                styles.errorMessageMobile,
                validationErrorMessage && styles.errorMessageContainerMargin,
              ]}
            >
              {validationErrorMessage && (
                <InlineNotification severity="error" role="alert" css={styles.validationErrorMessage}>
                  <Typography2 component="p">{validationErrorMessage}</Typography2>
                </InlineNotification>
              )}
            </Grid>
          )}
        </Grid>
      </ViewFormVisibilityTracker>
    );
  } else {
    return null;
  }
};

export default OnboardingPageContent;
